import React, { useEffect, useState } from 'react';

import { NativeResources } from '@ariksa/abac/api';
import { Box, HStack, Stack } from '@chakra-ui/react';
import dayjs from 'dayjs';
import { filter, map } from 'lodash';
import { useNavigate, useParams } from 'react-router-dom';

import { Card, PageHeaderWithIcon } from 'components/DataDisplay';
import { BorderedBox } from 'components/DataDisplay/Box/BorderedBox';
import { FilterLabelWrapper } from 'components/DataDisplay/Box/BorderedBox/FilterLabelWrapper';
import { Select } from 'components/DataEntry';
import { CustomDayPicker } from 'components/DataEntry/Date';
import { BreadcrumbListOptions, SuddenActivityIcon } from 'components/index';
import { Breadcrumbs } from 'components/Navigation/Breadcrumbs';
import { ActiveResourceProvider } from 'containers/ActiveCloudResource/hooks/context/ActiveResourceProvider';
import { useSearchParamFunctions } from 'containers/App/hooks/useSearchParamFunctions';
import { useSearchParams } from 'hooks/useSearchParams';

import { insightCategoryOptions } from '../../utils';

import { ActivityGroupResourcesTable } from './ActivityGroupResourcesTable';

export const ActivityGroupResources: React.FC = () => {
  const { group_id } = useParams();
  const params = useSearchParams<{
    date: string;
    insightCategories: string;
    groupName: string;
  }>();
  const navigate = useNavigate();
  const { updateValueInURL } = useSearchParamFunctions();
  const [date, setDate] = useState('');
  const [insightCategories, setInsightCategories] = useState<
    Record<string, any>[]
  >([]);

  useEffect(() => {
    setDate(params.date || dayjs().utc().format());
  }, [params.date]);

  useEffect(() => {
    setInsightCategories(
      params.insightCategories
        ? filter(insightCategoryOptions, option =>
            params.insightCategories.includes(option.value),
          )
        : [],
    );
  }, [params.insightCategories]);

  const breadcrumbList: BreadcrumbListOptions[] = [
    {
      label: 'Data',
      onClick: () => navigate('/visibility/activity?tab=Data'),
    },
    {
      label: 'Activity Group - ' + params.groupName ?? '-',
    },
  ];

  return (
    <ActiveResourceProvider>
      <Card>
        <Stack spacing={4} h="full" w="full">
          <Breadcrumbs list={breadcrumbList} />
          <Box>
            <PageHeaderWithIcon
              label={params.groupName ?? 'Activity Group'}
              icon={<SuddenActivityIcon />}
            />
          </Box>
          <BorderedBox w="full" h="full" p={4} minH="500px">
            <Stack flex={1} w="full" h="full" spacing={5}>
              <HStack>
                <FilterLabelWrapper>FILTER BY</FilterLabelWrapper>
                <Box>
                  <Select
                    leftMessage="Insight"
                    isAFilter
                    isMulti
                    options={insightCategoryOptions}
                    value={insightCategories}
                    onChange={options =>
                      updateValueInURL(
                        'insightCategories',
                        options?.map(option => option?.value),
                      )
                    }
                    menuPortalTarget={document.body}
                  />
                </Box>
                <FilterLabelWrapper>DATE</FilterLabelWrapper>
                <CustomDayPicker
                  portal
                  dateProps={{
                    disabled: [
                      {
                        before: dayjs()
                          .subtract(90, 'day')
                          .startOf('day')
                          .toDate(),
                      },
                      { after: new Date() },
                    ],
                    mode: 'single' as any,
                    onSelect: (selected: any) => {
                      updateValueInURL(
                        'date',
                        dayjs(selected).utc().format().toString(),
                      );
                    },
                    selected: date,
                  }}
                />
              </HStack>
              <ActivityGroupResourcesTable
                groupId={group_id!}
                groupName={params.groupName}
                date={date}
                insightCategories={map(
                  insightCategories,
                  option => option.value,
                )}
                resourceType={NativeResources.SimpleStorageService}
              />
            </Stack>
          </BorderedBox>
        </Stack>
      </Card>
    </ActiveResourceProvider>
  );
};
